import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { SortingState } from '@tanstack/react-table';
import { Form, Formik } from 'formik';

import { Input, Modal, SingleSelect, Table } from '@/common';
import { Button, Spinner, Textarea } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { AuthContext, OfflineContext } from '@/contexts';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { PlaylistType } from '@/enums/PlaylistEnum';
import { getPlaylistColumns } from '@/helpers';
import { formatDurationToMinutes } from '@/helpers/date';
import { showToast, ToastType } from '@/helpers/toast';
import { Filters } from '@/pages/Playlists/Filters';
import { useGetAllOrganizations } from '@/services';
import {
  useCreatePlaylistMutation,
  useDeletePlaylistMutation,
  useGetAllPlaylists
} from '@/services/playlist.service';
import { useFilterStore } from '@/store';
import {
  AllowedPermissions,
  Organization,
  Playlist,
  PlaylistSimple,
  SelectOptionProps,
  TableData
} from '@/types';

import { playlistValidationSchema } from './PlaylistValidation';

type PlaylistProps = {
  playlistType?: PlaylistType;
};

export const Playlists = ({ playlistType = PlaylistType.MUSIC }: PlaylistProps) => {
  const [t] = useTranslation('common');
  const { currentUserData: currentUser, permissions: userPermissions } = useContext(AuthContext);
  const { isOnline, offlinePlaylists, downloadedPlaylistIds, removeOfflinePlaylist } =
    useContext(OfflineContext);
  const navigate = useNavigate();
  const { organizationFilter } = useFilterStore();

  const [search, setSearch] = useState<string>();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist>();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'Id', desc: false }]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchOrganization, setSearchOrganization] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>();
  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);
  const [isOrganizationFilterRequired, setIsOrganizationFilterRequired] = useState<boolean>(true);

  const hasPermissionViewOrganizations = userPermissions.includes(
    PERMISSION_TYPE.VIEW_ORGANIZATIONS
  );

  const [filters, setFilters] = useState<{
    organization: SingleValue<SelectOptionProps>;
  }>({
    organization: null
  });

  const getOrganizationId = () => {
    if (organizationFilter && organizationFilter.value) {
      return organizationFilter.value as number;
    } else if (filters.organization === null && currentUser?.organizations.length === 1) {
      return currentUser?.organizations[0].id as number;
    }
    return filters.organization?.value as number;
  };

  const {
    data: allOrganizations,
    fetchNextPage: fetchNextPageOrganizations,
    hasNextPage: hasNextPageOrganizations
  } = useGetAllOrganizations(
    PAGE_SIZE,
    searchOrganization,
    undefined,
    hasPermissionViewOrganizations
  );

  const {
    isLoading,
    data: allPlaylists,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetAllPlaylists(
    PAGE_SIZE,
    search,
    sorting.map((s) => s.id.charAt(0).toUpperCase() + s.id.slice(1)).toString(),
    sorting.some((s) => s.desc),
    getOrganizationId(),
    playlistType === PlaylistType.MIX ? PlaylistType.MIX : PlaylistType.MUSIC,
    isOnline
  );

  const { mutate: createPlaylist } = useCreatePlaylistMutation(queryClient, {
    onSuccess: () => {
      navigate(
        playlistType === PlaylistType.MIX
          ? PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_PLAYLISTS
          : PRIVATE_PAGE_ROUTES.PLAYLISTS
      );
      showToast(ToastType.SUCCESS, t('playlistSuccesfullyCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: deletePlaylist } = useDeletePlaylistMutation(queryClient, {
    onSuccess: async () => {
      const deletedPlaylistId = selectedPlaylist?.id;
      if (deletedPlaylistId) {
        try {
          await removeOfflinePlaylist(deletedPlaylistId);
        } catch {
          // Ignore offline cleanup errors and continue with normal flow.
        }
      }

      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_PLAYLISTS,
    editPermission: PERMISSION_TYPE.UPDATE_PLAYLISTS,
    deletePermission: PERMISSION_TYPE.DELETE_PLAYLISTS,
    viewPermission: PERMISSION_TYPE.VIEW_PLAYLISTS
  };

  const getOrganizationOptions = () => {
    return (
      allOrganizations?.pages.flatMap((page) =>
        page.data?.items.map((organization: Organization) => ({
          value: organization.id,
          label: organization.name
        }))
      ) || []
    );
  };

  useEffect(() => {
    const numberOfFilters = Object.values(filters).filter((value) => value !== null);
    setAppliedFiltersCounter(numberOfFilters.length);
  }, [filters]);

  useEffect(() => {
    if (organizationFilter && organizationFilter.value) {
      resetFilters();
      setIsOrganizationFilterRequired(false);
    } else if (filters.organization === null && currentUser?.organizations.length === 1) {
      resetFilters();
      setIsOrganizationFilterRequired(false);
    } else {
      setIsOrganizationFilterRequired(true);
    }
  }, [organizationFilter, filters.organization, currentUser]);

  const formatData = (): TableData[] => {
    return allPlaylists?.pages.flatMap((page) =>
      (page.data?.items ?? []).map((playlistData: PlaylistSimple) => ({
        ...playlistData,
        enabled: true,
        noOfAssets: playlistData.numberOfAsset,
        totalDuration: formatDurationToMinutes(playlistData.totalDuration)
      }))
    ) as TableData[];
  };

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleCreatePlaylist = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditPlaylist = (row: Playlist) => {
    let editPlanUrl: string;

    if (playlistType === PlaylistType.MIX) {
      editPlanUrl = PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_SINGLE_PLAYLIST.replace(
        ':playlistId',
        row.id.toString()
      );
    } else {
      editPlanUrl = PRIVATE_PAGE_ROUTES.SINGLE_PLAYLISTS.replace(':playlistId', row.id.toString());
    }
    navigate(editPlanUrl);
  };

  const handleDeletePlaylist = (row: Playlist) => {
    setIsOpenDeleteModal(true);
    setSelectedPlaylist(row);
  };

  const handleViewDetails = (row: Playlist) => {
    let editPlanUrl: string;

    if (playlistType === PlaylistType.MIX) {
      editPlanUrl = PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_SINGLE_PLAYLIST.replace(
        ':playlistId',
        row.id.toString()
      );
    } else {
      editPlanUrl = PRIVATE_PAGE_ROUTES.SINGLE_PLAYLISTS.replace(':playlistId', row.id.toString());
    }
    navigate(editPlanUrl);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedPlaylist(undefined);
  };

  const getInitialValues = () => {
    return {
      name: '',
      description: '',
      organizationId: getOrganizationId() ?? 0
    };
  };

  const handleSubmitForm = (values: {
    name: string;
    description: string;
    organizationId: number;
  }) => {
    setIsSubmitting(true);

    const payload = {
      name: values.name,
      description: values.description,
      organizationId: values.organizationId,
      playlistType: playlistType === PlaylistType.MIX ? PlaylistType.MIX : PlaylistType.MUSIC
    };

    createPlaylist(payload);
    setIsCreateModalOpen(false);
  };

  const resetFilters = () => {
    setIsFilterModalOpen(false);
    setFilters({ organization: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: Record<string, SelectOptionProps>) => {
    setIsFilterModalOpen(false);
    setFilters({
      organization: values.filterByOrganization as SelectOptionProps
    });
  };

  const handleCloseFiltersModal = () => {
    setIsFilterModalOpen(false);
  };

  //Offline mode: show only downloaded playlists
  if (!isOnline) {
    const offlineRows = offlinePlaylists.map((p) => ({
      id: p.id,
      name: p.name,
      description: p.description,
      enabled: true,
      noOfAssets: p.songs.length,
      totalDuration: formatDurationToMinutes(p.totalDuration),
      playlistAssets: [],
      playlistType: 0,
      organizationId: p.organizationId
    }));

    return (
      <div className='flex size-full flex-col items-center'>
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          <Table
            data={offlineRows as unknown as TableData[]}
            columns={getPlaylistColumns(downloadedPlaylistIds)}
            tableTitle={t('playlists')}
            permissions={{
              createPermission: '' as never,
              editPermission: '' as never,
              deletePermission: '' as never,
              viewPermission: '' as never
            }}
            handleViewDetails={(item) => handleViewDetails(item as Playlist)}
            hiddenColumnsMobile={['description']}
            mobileColumnOffset={5}
            sorting={sorting}
            setSorting={setSorting}
            fetchNextPage={() => undefined}
            hasNextPage={false}
            isFetchingNextPage={false}
            numberOfAppliedFilters={0}
          />
        </div>
      </div>
    );
  }

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          <Table
            data={formatData()}
            columns={getPlaylistColumns(downloadedPlaylistIds)}
            tableTitle={t('playlists')}
            addButtonLabel={t('addPlaylist')}
            permissions={permissions}
            search={searchCallback}
            handleCreate={handleCreatePlaylist}
            handleEdit={(item) => handleEditPlaylist(item as Playlist)}
            handleDelete={(item) => handleDeletePlaylist(item as Playlist)}
            handleViewDetails={(item) => handleViewDetails(item as Playlist)}
            handleOpenFilterModal={
              isOrganizationFilterRequired
                ? () => setIsFilterModalOpen(!isFilterModalOpen)
                : undefined
            }
            fetchNextPage={() => fetchNextPage()}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            hiddenColumnsMobile={['description']}
            mobileColumnOffset={5}
            numberOfAppliedFilters={appliedFiltersCounter}
            sorting={sorting}
            setSorting={setSorting}
          />
          {selectedPlaylist && (
            <ConfirmationModal
              isOpen={isOpenDeleteModal}
              message={t('deleteMessage', {
                item: `${selectedPlaylist.name}`
              })}
              onClose={handleCloseDeleteModal}
              onConfirm={() => deletePlaylist(selectedPlaylist.id)}
            />
          )}
          {isCreateModalOpen && (
            <Modal
              isOpen={isCreateModalOpen}
              onClose={() => setIsCreateModalOpen(false)}
              modalClasses='w-full md:w-[500px]'
              label={t('createPlaylist')}
              height=''>
              <div className='inline-flex size-full flex-col items-center justify-center'>
                <Formik
                  enableReinitialize
                  onSubmit={handleSubmitForm}
                  initialValues={getInitialValues()}
                  validateOnMount
                  validationSchema={playlistValidationSchema}>
                  {({ errors, touched, isValid, setFieldValue, values, setFieldTouched }) => (
                    <Form className='size-full overflow-hidden scrollbar-none'>
                      <div className='flex w-full flex-col gap-y-2'>
                        <SingleSelect
                          id='organizationId'
                          options={getOrganizationOptions()}
                          handleChange={(option: SelectOptionProps | null) =>
                            setFieldValue('organizationId', option?.value ?? 0)
                          }
                          value={
                            getOrganizationOptions().find(
                              (org) => org.value === values.organizationId
                            ) || null
                          }
                          isSearchable={true}
                          isClearable={true}
                          isRequired={true}
                          label={t('organization')}
                          placeholder={t('organization')}
                          handleInputChange={setSearchOrganization}
                          fetchNextPage={fetchNextPageOrganizations}
                          hasNextPage={hasNextPageOrganizations}
                          className='w-full'
                          onBlur={() => setFieldTouched('organizationId', true)}
                          errorMessage={errors.organizationId as string}
                          isInvalid={!!errors.organizationId && !!touched.organizationId}
                        />
                        <Input
                          type='text'
                          id='name'
                          label={t('name')}
                          isRequired
                          errorMessage={touched.name ? (errors.name as string) : ''}
                          isInvalid={!!(errors.name && touched.name)}
                        />
                        <Textarea
                          id='description'
                          label={t('description')}
                          errorMessage={errors.description as string}
                          className='h-20'
                        />
                      </div>
                      <div className='mt-2 flex justify-end'>
                        <Button
                          type='submit'
                          className='w-full md:w-fit'
                          variant={ButtonVariant.PRIMARY}
                          disabled={isSubmitting || !isValid}>
                          {t('add')}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </Modal>
          )}
          {isFilterModalOpen && (
            <Filters
              apply={applyFilters}
              reset={resetFilters}
              onClose={handleCloseFiltersModal}
              filterByOrganization={filters.organization}
            />
          )}
        </div>
      )}
    </div>
  );
};
